import React from "react";
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Typography,
  Stack,
} from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import FilterListRoundedIcon from "@mui/icons-material/FilterListRounded";
import CalendarTodayRoundedIcon from "@mui/icons-material/CalendarTodayRounded";
import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";

const MotionBox = motion(Box);

const glassMixin = {
  backdropFilter: "blur(20px) saturate(180%)",
  WebkitBackdropFilter: "blur(20px) saturate(180%)",
  background: "rgba(255,255,255,0.72)",
  border: "1px solid rgba(255,255,255,0.9)",
  boxShadow:
    "0 8px 32px rgba(99,102,241,0.07), 0 1px 0 rgba(255,255,255,0.95) inset",
};

const selectSx = (active) => ({
  borderRadius: "14px",
  fontSize: "0.85rem",
  fontWeight: 600,
  color: "#111827",
  background: active ? "rgba(99,102,241,0.06)" : "rgba(255,255,255,0.6)",
  backdropFilter: "blur(8px)",
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: active ? "rgba(99,102,241,0.4)" : "rgba(0,0,0,0.1)",
    transition: "border-color 0.2s",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(99,102,241,0.5)",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "#6366f1",
    borderWidth: 1.5,
  },
});

const Filter = ({ filters, setFilters, data }) => {
  const hasActiveFilters = filters.term || filters.session;
  const handleClear = () => setFilters({ term: "", session: "" });

  return (
    <MotionBox
      initial={{ opacity: 0, y: -16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 2,
        p: "14px 20px",
        borderRadius: "20px",
        flexWrap: "wrap",
        mb: 3,
        ...glassMixin,
      }}
    >
      {/* icon + label */}
      <Stack direction="row" alignItems="center" spacing={1.5}>
        <Box
          sx={{
            width: 34,
            height: 34,
            borderRadius: "11px",
            background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0 4px 12px rgba(99,102,241,0.35)",
          }}
        >
          <FilterListRoundedIcon sx={{ color: "#fff", fontSize: 16 }} />
        </Box>
        <Typography
          sx={{
            fontFamily: "'Syne', sans-serif",
            fontWeight: 700,
            color: "#4b5563",
            letterSpacing: "0.1em",
            fontSize: "0.66rem",
          }}
        >
          FILTERS
        </Typography>
      </Stack>

      {/* divider */}
      <Box
        sx={{
          width: 1,
          height: 28,
          background: "rgba(0,0,0,0.08)",
          display: { xs: "none", sm: "block" },
        }}
      />

      {/* Term */}
      <FormControl size="small" sx={{ minWidth: 160 }}>
        <InputLabel
          sx={{
            fontSize: "0.8rem",
            fontWeight: 600,
            color: "#6b7280",
            "&.Mui-focused": { color: "#6366f1" },
          }}
        >
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <AccessTimeRoundedIcon sx={{ fontSize: 13 }} />
            <span>Term</span>
          </Stack>
        </InputLabel>
        <Select
          value={filters.term || ""}
          label="Term"
          onChange={(e) => setFilters({ ...filters, term: e.target.value })}
          sx={selectSx(!!filters.term)}
        >
          <MenuItem value="">
            <Typography variant="body2" color="text.secondary" fontWeight={500}>
              All Terms
            </Typography>
          </MenuItem>
          {data?.terms?.map((t) => (
            <MenuItem key={t.id} value={t.id}>
              <Typography variant="body2" fontWeight={600}>
                {t.term}
              </Typography>
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Session */}
      <FormControl size="small" sx={{ minWidth: 180 }}>
        <InputLabel
          sx={{
            fontSize: "0.8rem",
            fontWeight: 600,
            color: "#6b7280",
            "&.Mui-focused": { color: "#6366f1" },
          }}
        >
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <CalendarTodayRoundedIcon sx={{ fontSize: 13 }} />
            <span>Session</span>
          </Stack>
        </InputLabel>
        <Select
          value={filters.session || ""}
          label="Session"
          onChange={(e) => setFilters({ ...filters, session: e.target.value })}
          sx={selectSx(!!filters.session)}
        >
          <MenuItem value="">
            <Typography variant="body2" color="text.secondary" fontWeight={500}>
              All Sessions
            </Typography>
          </MenuItem>
          {data?.sessions?.map((s) => (
            <MenuItem key={s.id} value={s.id}>
              <Typography variant="body2" fontWeight={600}>
                {s.date}
              </Typography>
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* clear chip */}
      <AnimatePresence>
        {hasActiveFilters && (
          <MotionBox
            initial={{ opacity: 0, scale: 0.7, x: -10 }}
            animate={{ opacity: 1, scale: 1, x: 0 }}
            exit={{ opacity: 0, scale: 0.7, x: -10 }}
            transition={{ duration: 0.2 }}
          >
            <Chip
              label="Clear"
              deleteIcon={<CloseRoundedIcon style={{ fontSize: 14 }} />}
              onDelete={handleClear}
              onClick={handleClear}
              size="small"
              sx={{
                background: "rgba(239,68,68,0.08)",
                color: "#ef4444",
                fontWeight: 700,
                fontSize: "0.72rem",
                border: "1px solid rgba(239,68,68,0.22)",
                borderRadius: "8px",
                "& .MuiChip-deleteIcon": { color: "#ef4444" },
                "&:hover": { background: "rgba(239,68,68,0.15)" },
              }}
            />
          </MotionBox>
        )}
      </AnimatePresence>
    </MotionBox>
  );
};

export default Filter;
